import React, { FormEvent, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material//CheckCircle';
import CheckCircleOutlinedIcon from '@mui/icons-material//CheckCircleOutlined';
import { Button, Grid, Paper, TextField, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { isNull, isUndefined, noop } from 'lodash';
import { Link as RouterLink, useParams } from 'react-router-dom';
import validate from 'validate.js';
import { ERROR_400, ERROR_401, ERROR_404 } from 'common/config/api';
import { logIn } from 'common/config/urls';
import * as messages from 'common/constants/messages/validations';
import {
  NEW_PASSWORD_SECURE_ACCOUNT,
  RESET_PASSWORD,
  SELECT_PASSWORD,
  STRONG_PASSWORD_MESSAGE,
} from 'common/constants/messages/validations';
import { Alert, Logo } from 'components';
import { Severity } from 'components/Alert/type';
import { useSendPasswordChange } from 'services/hooks/auth';
import { capitalizeString, differentThanValidator, excludeWhiteSpacesValidator, toString } from 'utilities';
import {
  CONSTRAINTS_PASSWORD_CHANGE,
  DEFAULT_ERROR_MESSAGE,
  PASSWORD_CHECKS,
  REGEXP_LIST,
} from './constants';
import {
  BackendResponse,
  FormFieldName,
  FormStateProps,
  ResetPasswordConfirmFormProps,
} from './ResetPasswordConfirmForm.props';
import {
  StyledBackImage,
  StyledButton,
  StyledContent,
  StyledContentImage,
  StyledForm,
  StyledFormContainer,
  StyledHeaderLogo,
  StyledImage,
  StyledLink,
  StyledLinkContainer,
  StyledQuoteContainer,
  StyledRoot,
} from './ResetPasswordConfirmForm.styles';

validate.validators.excludeWhiteSpaces = excludeWhiteSpacesValidator;
validate.validators.differentThan = differentThanValidator;

const ResetPasswordConfirmForm = () => {
  const { uid, token } = useParams<{ uid: string; token: string }>();
  const query = new URLSearchParams(window.location.search);
  const isAfterActivation = query.get('afterActivation') === 'true';
  const sendPasswordReset = useSendPasswordChange();
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const [formState, setFormState] = useState<FormStateProps>({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  });

  const [severity, setSeverity] = useState<Severity>('error');

  const hasError = (field: FormFieldName) => !!formState.errors[field] && !!formState.errors[field].length;

  const checkValuePresence = (field: FormFieldName) => {
    const value = formState.values[field];
    let existValue = true;
    if (!value) {
      existValue = false;
      switch (field) {
        case 'password':
          formState.errors[field] = [messages.PASSWORD_EMPTY('New Password')];
          break;
        case 'passwordConfirmation':
          formState.errors.passwordConfirmation = [messages.PASSWORD_EMPTY('New Password confirm')];
          break;
        default:
          break;
      }
    }
    return existValue;
  };

  const checkPasswordsEquality = () => {
    setIsLoading(false);
    if (!checkValuePresence('password') || !checkValuePresence('passwordConfirmation')) {
      setFormState({ ...formState });
      setSeverity('error');
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      setIsAlertVisible(true);
    } else if (formState.values.password !== formState.values.passwordConfirmation) {
      setSeverity('error');
      setErrorMessage(messages.PASSWORD_MISMATCH);
      setIsAlertVisible(true);
      formState.errors.password = [''];
      formState.errors.passwordConfirmation = [''];
    } else {
      setIsAlertVisible(false);
    }
  };

  const setBackendError = (response: BackendResponse) => {
    setIsAlertVisible(true);
    setSeverity('error');

    switch (toString(response.statusCode)) {
      case ERROR_401:
        setErrorMessage(response.body.detail);
        break;
      case ERROR_400:
        setErrorMessage(messages.PASSWORD_USER_OR_TOKEN_NOT_FOUND);
        break;
      case ERROR_404:
        setErrorMessage(messages.PASSWORD_URL_USED);
        break;
      default:
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
        break;
    }
  };

  const redirectToLoginAfterNSeconds = (seconds: number) => {
    setTimeout(() => {
      window.location.href = logIn;
    }, seconds * 1000);
  };

  const handleSetPassword = async (event: FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsAlertVisible(false);
    checkPasswordsEquality();

    if (formState.isValid && formState.values.password && formState.values.passwordConfirmation && uid && token) {
      setIsLoading(true);
      const response = await sendPasswordReset(
        formState.values.password,
        formState.values.passwordConfirmation,
        uid,
        token
      );
      setIsLoading(false);
      if (isNull(response)) {
        // status code 204 => null body
        setIsAlertVisible(true);
        setSeverity('success');
        setErrorMessage(messages.PASSWORD_CHANGE_CONFIRM);
        redirectToLoginAfterNSeconds(5);
      } else {
        setBackendError(response);
      }
    } else {
      setIsAlertVisible(true);
    }
    setIsLoading(false);
  };

  const updateErrors = (errors: Record<string, string[]>, fieldName: string) => {
    setIsAlertVisible(!!errors?.[fieldName] && errors[fieldName].length > 0);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const tmpFormState = { ...formState };
    const fieldName = event.target.name as FormFieldName;
    const fieldValue = event.target.value;
    const errors = validate({ ...formState.values, [fieldName]: fieldValue }, CONSTRAINTS_PASSWORD_CHANGE);

    tmpFormState.values[fieldName] = fieldValue;
    tmpFormState.touched[fieldName] = true;
    tmpFormState.isValid = isUndefined(errors);
    tmpFormState.errors = errors ?? {};

    setFormState(tmpFormState);
    updateErrors(errors, fieldName);
  };

  const handleFocusOut = () => {
    checkPasswordsEquality();
  };

  const checkConditionByIndex = (index: number) => {
    const { password } = formState.values;
    const { passwordConfirmation } = formState.values;

    if (!password || password.length <= 0) {
      return false;
    }

    // Password equality check is the last item
    if (index === PASSWORD_CHECKS.length - 1) {
      return password === passwordConfirmation;
    }

    // Regular expression checks
    return REGEXP_LIST[index].test(password);
  };

  const getCheckIcon = (index: number) => {
    const style = { fill: 'white' };
    const dataTestId = `img-${index}`;
    const id = `icon-id-${index}`;
    if (checkConditionByIndex(index)) {
      return <CheckCircleIcon id={id} data-testid={dataTestId} style={style} />;
    }
    return <CheckCircleOutlinedIcon id={id} data-testid={dataTestId} style={style} />;
  };

  const checklist = PASSWORD_CHECKS.map((elem, index) => (
    <ListItem key={elem}>
      <ListItemIcon>{getCheckIcon(index)}</ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Typography component="span">
            <Box color="white" fontWeight={500}>
              {elem}
            </Box>
          </Typography>
        }
      />
    </ListItem>
  ));

  const formIsValid = () => {
    for (let i = 0; i < checklist.length; i += 1) {
      if (!checkConditionByIndex(i)) {
        return false;
      }
    }
    return true;
  };

  return (
    <StyledRoot container>
      <StyledBackImage item lg={6}>
        <StyledContentImage>
          <StyledImage alt="Background" src="/images/login-bg.jpg" />
        </StyledContentImage>
        <StyledHeaderLogo>
          <Typography variant="h1">
            <RouterLink to="/">
              <Logo variation="white" />
            </RouterLink>
          </Typography>
        </StyledHeaderLogo>
        <StyledQuoteContainer>
          <Typography variant="h2">
            In order to be secure enough,
            <br />
            your new password should have at least:
          </Typography>
          <List>{checklist}</List>
        </StyledQuoteContainer>
      </StyledBackImage>
      <StyledFormContainer item lg={6} xs={12}>
        {isAfterActivation && (
          <>
            <CheckCircleOutlinedIcon className="message-box__inside-icon-ok" data-testid="message-box__icon" />
            <Typography style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>
              <strong>Account activated</strong>
            </Typography>
          </>
        )}
        <StyledForm>
          <Typography variant="h1" color="primary">
            {isAfterActivation ? capitalizeString(SELECT_PASSWORD) : RESET_PASSWORD}
          </Typography>
          <Typography style={{ marginTop: '1rem', fontSize: '1.2rem' }}>
            {isAfterActivation ? NEW_PASSWORD_SECURE_ACCOUNT : STRONG_PASSWORD_MESSAGE}
          </Typography>
          <Alert isAlertVisible={isAlertVisible} severity={severity}>
            <>{errorMessage}</>
          </Alert>
          <form
            id="password-confirm-form"
            onSubmit={e => {
              handleSetPassword(e).catch(noop);
            }}>
            <TextField
              id="password"
              name="password"
              label="New Password"
              fullWidth
              type="password"
              className="scar-textField"
              error={isAlertVisible && hasError('password')}
              helperText={isAlertVisible && hasError('password') ? formState.errors.password?.[0] : null}
              onChange={handleChange}
              defaultValue=""
            />
            <TextField
              id="password-confirmation"
              name="passwordConfirmation"
              label="Repeat New Password"
              fullWidth
              type="password"
              className="scar-textField"
              error={isAlertVisible && hasError('passwordConfirmation')}
              helperText={
                isAlertVisible && hasError('passwordConfirmation') ? formState.errors.passwordConfirmation?.[0] : null
              }
              onChange={handleChange}
              onBlur={handleFocusOut}
              defaultValue=""
            />
            <StyledButton
              id="submit-action-btn"
              variant="contained"
              color="secondary"
              fullWidth
              type="submit"
              disabled={!formIsValid()}
              onClick={e => {
                handleSetPassword(e).catch(noop);
              }}>
              {isLoading ? <CircularProgress size={28} color="inherit" /> : 'Create new password'}
            </StyledButton>
          </form>
        </StyledForm>
      </StyledFormContainer>
    </StyledRoot>
  );
};

export default ResetPasswordConfirmForm;
